import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  useResendOtpMutation,
  useResetPasswordConfirmMutation,
  useResetPasswordMutation,
} from "reduxFiles/api/authApi";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "reduxFiles/hooks";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { useEffect, useState } from "react";
import OtpInput from "components/otp/OtpInput";
import { formatTime } from "services/helper";
import { motion, AnimatePresence } from "framer-motion";
import Button from "components/button/Button";
import { HiArrowLeft, HiArrowRight } from "react-icons/hi2";
import PhoneInput from "components/input/PhoneInput";
import InputField from "components/fields/InputField";
import { countries } from "i18n";

export default function ResetPassword() {
  const RESEND_TIME = 120;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isRtl = document.documentElement.dir === "rtl";

  // apis
  const [resetPassword, { isLoading: sendLoading }] =
    useResetPasswordMutation();
  const [resetPasswordConfirm, { isLoading: resetLoading }] =
    useResetPasswordConfirmMutation();
  const [resendOtp, { isLoading: resendLoading }] = useResendOtpMutation();

  // states
  const [step, setStep] = useState<"send" | "confirm">("send");
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(RESEND_TIME);

  // timer
  useEffect(() => {
    if (step !== "confirm") return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

  useEffect(() => {
    if (step === "confirm") {
      setTimer(RESEND_TIME);
    }
  }, [step]);

  // forms
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<{ mobile: string }>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      mobile: "",
    },
  });

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    watch: watchReset,
    formState: { errors: resetErrors },
  } = useForm<{ password: string; confirmPassword: string }>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const passwordValue = watchReset("password");
  const mobileValue = watch("mobile");

  // send otp
  const onSendOtp = async ({ mobile }: { mobile: string }) => {
    try {
      await resetPassword(mobile).unwrap();
      setMobileNumber(mobile);
      setStep("confirm");
    } catch (error) {
      console.error(error);
    }
  };

  // resend
  const handleResend = async () => {
    try {
      await resendOtp({
        mobile: mobileNumber,
        type: "FORGET_PASSWORD",
      }).unwrap();
      setTimer(RESEND_TIME);
    } catch (err) {
      console.error(err);
    }
  };

  // confirm
  const onReset = async ({ password }: { password: string }) => {
    if (otp.length !== 4) {
      dispatch(
        addNotification({
          type: "error",
          message: t("auth.resetPassword.invalidCode"),
        })
      );
      return;
    }

    try {
      await resetPasswordConfirm({
        mobile: mobileNumber,
        password: password,
        token: otp,
      }).unwrap();

      dispatch(
        addNotification({
          type: "success",
          message: t("auth.resetPassword.success"),
        })
      );

      navigate("/auth/sign-in");
    } catch (error) {
      console.error(error);
    }
  };

  // Phone change handler
  const handlePhoneChange = (phoneNumber: string) => {
    const cleaned = phoneNumber.replace(/[^\d+]/g, "");
    setValue("mobile", cleaned, { shouldValidate: true });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="flex h-full w-full items-center justify-center"
    >
      <div className="w-full max-w-md px-2 sm:px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="mb-4 sm:mb-6"
        >
          <div className="mb-1 flex items-center gap-2">
            <div className="h-1 w-8 rounded-full bg-gradient-to-r from-brand-500 to-indigo-500" />
            <span className="text-xs font-medium uppercase tracking-wider text-brand-500 dark:text-brand-100">
              {t("auth.resetPassword.badge") || "Security"}
            </span>
          </div>
          <h4 className="text-2xl font-bold text-navy-700 dark:text-white sm:text-3xl">
            {t("auth.resetPassword.title")}
          </h4>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("auth.resetPassword.description")}
          </p>
        </motion.div>

        {/* Forms Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative"
        >
          <AnimatePresence mode="wait">
            {/* STEP 1 - Send OTP */}
            {step === "send" && (
              <motion.form
                key="send"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.4 }}
                onSubmit={handleSubmit(onSendOtp)}
                noValidate
                className="space-y-3"
              >
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("auth.fields.mobile")}
                  </label>
                  <PhoneInput
                    countries={countries}
                    placeholder={t("auth.placeholders.mobile") || "09123456789"}
                    value={mobileValue}
                    onChange={handlePhoneChange}
                    selectPosition="end"
                    error={errors.mobile}
                    readOnly={false}
                    disabled={false}
                  />
                  {errors.mobile && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.mobile.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={sendLoading}
                  className="mt-1"
                >
                  {t("auth.resetPassword.sendCode")}
                </Button>
              </motion.form>
            )}

            {/* STEP 2 - Confirm OTP & Reset Password */}
            {step === "confirm" && (
              <motion.form
                key="confirm"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
                onSubmit={handleResetSubmit(onReset)}
                noValidate
                className="space-y-2"
              >
                {/* Back Button */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    startIcon={isRtl ? <HiArrowRight /> : <HiArrowLeft />}
                    onClick={() => setStep("send")}
                    className="w-fit"
                  >
                    {t("buttons.back")}
                  </Button>

                  {/* Resend Timer & Button */}
                  <div className="flex items-center justify-between rounded-lg">
                    {timer > 0 ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t("auth.resetPassword.resendIn", {
                          seconds: formatTime(timer),
                        })}
                      </p>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        loading={resendLoading}
                        onClick={handleResend}
                        disabled={resendLoading || timer > 0}
                        className="text-xs"
                      >
                        {t("auth.resetPassword.resendCode")}
                      </Button>
                    )}
                  </div>
                </div>

                {/* OTP Input */}
                <div className="space-y-2">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t("auth.signUp.otpSent", { name: mobileNumber })}
                    </p>
                  </div>

                  <OtpInput
                    length={4}
                    onChange={setOtp}
                    onComplete={() => {}}
                  />
                </div>

                {/* New Password */}
                <InputField
                  label={t("auth.resetPassword.fields.newPassword")}
                  placeholder={t("auth.placeholders.newPassword")}
                  id="password"
                  type="password"
                  variant="auth"
                  extra="mb-0"
                  {...registerReset("password", {
                    required: t("validation.required"),
                    minLength: {
                      value: 8,
                      message: t("validation.minLength", { min: 8 }),
                    },
                  })}
                  state={resetErrors.password ? "error" : ""}
                  errorMessage={resetErrors.password?.message}
                />

                {/* Confirm Password */}
                <InputField
                  label={t("auth.resetPassword.fields.confirmPassword")}
                  placeholder={t("auth.placeholders.confirmPassword")}
                  id="confirmPassword"
                  type="password"
                  variant="auth"
                  extra="mb-0"
                  {...registerReset("confirmPassword", {
                    required: t("validation.required"),
                    validate: (value) =>
                      value === passwordValue || t("validation.passwordMatch"),
                  })}
                  state={resetErrors.confirmPassword ? "error" : ""}
                  errorMessage={resetErrors.confirmPassword?.message}
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="success"
                  size="lg"
                  fullWidth
                  loading={resetLoading}
                >
                  {t("auth.resetPassword.submit")}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-4 flex flex-wrap items-center justify-center gap-1 border-t border-gray-200 pt-4 dark:border-navy-700"
        >
          <span className="text-xs text-gray-700 dark:text-gray-400">
            {t("auth.signUp.alreadyHaveAccount")}
          </span>
          <Link
            to="/auth/sign-in"
            className="text-xs font-medium text-brand-500 transition-all hover:text-brand-600 hover:underline dark:text-brand-100"
          >
            {t("auth.signUp.backToSignIn")}
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
}
